import { ContentService } from "@Api/ContentService";
import { CookieManager } from "./CookieManager";
import { Tenant } from "./Tenant";
import { TextResource } from "./TextResource";

export class DataPrivacyPolicy
{
	// MEMBER VARIABLES
	public DataPrivacyPolicyCookie = CookieManager.Cookies.DataPrivacyPolicy;
	public RequiredCountry = null;
	public VisitorCookie = CookieManager.Cookies.Visitor;

	constructor()
	{
	}

	// PUBLIC METHODS
	/// <summary>Gets or sets a value indicating whether to display the link.</summary>
	public get DisplayLink(): boolean
	{
		let display = false;

		if (this.MarketingApprovedSelection !== null || this.PerformanceApprovedSelection !== null)
		{
			display = true;
		}
		else
		{
			// one (or both) of the values not set, country determines whether to display this or not
			display = this.RequiredByCountry;
		}

		return display;
	}

	/// <summary>Gets approved countries from js variable</summary>
	public get EnabledCountries(): Array<number>
	{
		// @ts-expect-error this is defined on the dataprivacypolicy cookie facade
		if (typeof funDataPrivacyPolicyCountries !== 'undefined' && funDataPrivacyPolicyCountries !== null)
		{
			// @ts-expect-error this is defined on the dataprivacypolicy cookie facade
			return funDataPrivacyPolicyCountries;
		}
		else
		{
			return [];
		}
	}

	/// <summary>Gets or sets a value indicating whether to display the modal window.</summary>
	public get DisplayModal(): boolean
	{
		// display link must be true and one of the values must not be set
		const display = this.DisplayLink && (this.MarketingApprovedSelection === null || this.PerformanceApprovedSelection === null);

		return display;
	}

	/// <summary>Gets or sets a value indicating whether marketing is approved.</summary>
	public MarketingApproved = (): boolean =>
	{
		let approved = true;

		if (this.MarketingApprovedSelection !== null)
		{
			// use the actual value!
			approved = this.MarketingApprovedSelection;
		}
		else if (this.RequiredByCountry)
		{
			// required by country so it is not approved
			approved = false;
		}

		return approved;
	};

	/// <summary>Gets or sets a value indicating whether marketing tracking is approved.</summary>
	public get MarketingApprovedSelection(): boolean
	{
		let approved = null;

		const approvedValue = CookieManager.GetCookie(this.DataPrivacyPolicyCookie.Key, this.DataPrivacyPolicyCookie.MarketingTrackingApproved);

		if (approvedValue !== null && approvedValue.length > 0)
		{
			approved = approvedValue === '1';
		}

		return approved;
	}

	/// <summary>Gets or sets a value indicating whether performance is approved.</summary>
	public get PerformanceApproved(): boolean
	{
		let approved = true;

		if (this.PerformanceApprovedSelection !== null)
		{
			// use the action selection
			approved = this.PerformanceApprovedSelection;
		}
		else if (this.RequiredByCountry)
		{
			// required by country so it is not approved
			approved = false;
		}

		return approved;
	}

	/// <summary>Gets or sets a value indicating whether performance tracking is approved.</summary>
	public get PerformanceApprovedSelection(): boolean
	{
		let approved = null;

		const approvedValue = CookieManager.GetCookie(this.DataPrivacyPolicyCookie.Key, this.DataPrivacyPolicyCookie.PerformanceTrackingApproved);

		if (approvedValue !== null && approvedValue.length > 0)
		{
			approved = approvedValue === '1';
		}

		return approved;
	}

	/// <summary>Gets or sets a value indicating whether approval is required by the current country.</summary>
	public get RequiredByCountry(): boolean
	{
		if (this.RequiredCountry === null)
		{
			let countryID = 1; // default to US
			const countryIDValue = CookieManager.GetCookie(this.VisitorCookie.Key, this.VisitorCookie.CountryID);

			this.RequiredCountry = false;

			if (countryIDValue !== null && countryIDValue.length > 0)
			{
				const parsedCountryID = parseInt(countryIDValue);

				if (!isNaN(parsedCountryID))
				{
					countryID = parsedCountryID;
				}
			}

			let billCountryID = countryID; // default to the country selection
			const billCountryIDValue = CookieManager.GetCookie(this.VisitorCookie.Key, this.VisitorCookie.BillCountryID);

			if (billCountryIDValue !== null && billCountryIDValue.length > 0)
			{
				// try to use the value from the cookie
				const parsedBillCountryID = parseInt(billCountryIDValue);
				if (!isNaN(parsedBillCountryID))
				{
					billCountryID = parsedBillCountryID;
				}
			}

			let shipCountryID = countryID; // default to the country selection
			const shipCountryIDValue = CookieManager.GetCookie(this.VisitorCookie.Key, this.VisitorCookie.ShipCountryID);

			if (shipCountryIDValue !== null && shipCountryIDValue.length > 0)
			{
				// try to use the value from the cookie
				const parsedShipCountryID = parseInt(shipCountryIDValue);

				if (!isNaN(parsedShipCountryID))
				{
					shipCountryID = parsedShipCountryID;
				}
			}

			if (this.EnabledCountries.length > 0)
			{
				// loop through the countries to see if billing or shipping requires it!
				for (let countryIndex = 0; countryIndex < this.EnabledCountries.length; countryIndex++)
				{
					const currentCountry = this.EnabledCountries[countryIndex];

					if (currentCountry === billCountryID || currentCountry === shipCountryID)
					{
						// shipping or billing country requires it!
						this.RequiredCountry = true;
						break;
					}
				}
			}
		}

		return this.RequiredCountry;
	}

	public TriggerContentCallbacks = (): void =>
	{
		if (typeof ContentService.Response !== "undefined")
		{
			if (ContentService.Response.DisplayDataPrivacyPolicyLink)
			{
				this.AddCookieSettingsLink();
			}

			if (ContentService.Response.DisplayDataPrivacyPolicyModal)
			{
				CookieManager.SetCookie(CookieManager.Cookies.DelayModal.Key, "3");
				CookieManager.SetCookie(CookieManager.Cookies.SeenModal.Key, "false");
				this.OpenCookieSettings();
			}
		}
	};

	/**
	 * Add cookie settings link to the footer
	 */
	public AddCookieSettingsLink = (): void =>
	{
		const linkNode: HTMLAnchorElement = document.createElement("a");
		linkNode.id = "lnkCookieSettings";
		linkNode.href = "#";
		linkNode.innerHTML = "<br />" + TextResource.GetTextByCulture("Client_CookieModal_CookieSettings");
		const secure = document.documentElement.classList.contains("secure");

		if (secure)
		{
			const secureCopyright = document.querySelector("footer .copy");

			if (secureCopyright !== null)
			{
				secureCopyright.innerHTML += `&nbsp;&nbsp${linkNode.outerHTML}`;
			}
		}
		else
		{
			const prvLnk = document.querySelector(".prvlnk");

			if (prvLnk !== null)
			{
				prvLnk.insertAdjacentElement("afterend", linkNode);
			}
		}

		const cookieLink = document.getElementById(linkNode.id);

		cookieLink.addEventListener("click", (e: Event) =>
		{
			e.preventDefault();
			this.OpenCookieSettings();
		});
	};

	/**
	 * Open the cookie settings screen
	 */
	public OpenCookieSettings = (): void =>
	{
		const contentNode = document.createElement("div");
		contentNode.innerHTML = `
			<div class="offcanvas offcanvas-bottom bg-light h-auto cookieSettings" tabindex="-1" id="cookieSettings" data-bs-backdrop="false" aria-labelledby="cookieSettingsLabel">
				<div class="offcanvas-header">
					<span id="cookieSettingsLabel" class="visually-hidden offcanvas-title">${TextResource.GetTextByCulture("Client_CookieModal_CookieSettings")}</span>
					<button id="cookieSettingsClose" type="button" class="btn-close ms-auto text-reset" data-bs-dismiss="offcanvas" aria-label="Close"></button>
				</div>
				<div class="offcanvas-body">
					<div class="container-xl">
						<div class="row flex-column flex-md-row align-items-center">
							<div class="col">
								<div class="cookieSettings__text">
									<p><strong>${Tenant.WebsiteName}</strong> ${TextResource.GetTextByCulture("Client_CookieModal_Sentence")} <a href="/privacy-policy.html">${TextResource.GetTextByCulture("Client_CookieModal_PrivacyCookiesText")}</a>.</p>
								</div>
							</div>
							<div class="col-auto">
								<div class="cookieSettings__link">
									<a id="cookieSettingsAccept" href="javascript:void(0)" class="btn btn-primary">${TextResource.GetTextByCulture("Client_CookieModal_AceeptContinue")}</a>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		`;
		const cookieSettings = contentNode.firstElementChild;
		document.body.appendChild(cookieSettings);

		import("bootstrap/js/dist/offcanvas")
			.then((OffCanvas) =>
			{
				const offCanvas = new OffCanvas.default(cookieSettings);
				offCanvas.show();
			});

		document.querySelector("#cookieSettingsAccept").addEventListener("click", () =>
		{
			this.UpdateCookieSettings(true, true);
		});

		document.querySelector("#cookieSettingsClose").addEventListener("click", () =>
		{
			this.UpdateCookieSettings(false, false);
		});
	};

	public UpdateCookieSettings = (performance: boolean, marketing: boolean): void =>
	{
		const privacyCookie = CookieManager.Cookies.DataPrivacyPolicy;

		CookieManager.SetInCookie(privacyCookie.Key, privacyCookie.MarketingTrackingApproved, marketing ? "1" : "0");
		CookieManager.SetInCookie(privacyCookie.Key, privacyCookie.PerformanceTrackingApproved, performance ? "1" : "0");

		window.location.reload();
	};
}